import { createSlice } from '@reduxjs/toolkit'
import {
  getOIDField,
  updateSelectedSegmentGraphics
} from '../controller/esriMapController'

const initialState = {
  mapLoaded: false,
  manualStreetMode: false,
  selectedFeature: null,
  selectedFeaturesArr: [],
  openStreetMixDialog: false,
  showCircularProgress: false,
  showPopup: false,
  criteriaElmScrollTop: 0,
  defaultModalTiers: {
    Pedestrian: 0,
    Bicycle: 0,
    Transit: 0,
    Auto: 0,
    Freight: 0
  },
  showResetTiersModal: false,
  popupExpanded: true,
  showToolbox: true,
  toolboxExpanded: true,
  showStreetmixToaster: false,
  showSegmentAddedToaster: false,
  roadScoreType: 'Weakest Link Score',
  downloadDataUrl: '',
  reportHeaderDataUrl: '',
  reportTickImgDataUrl: '',
  reportPedestrialImgUrl: '',
  reportBicycleImgUrl: '',
  reportAutoImgUrl: '',
  reportTransitImgUrl: '',
  reportFreightImgUrl: '',
  shareableURL: false,
  firstAppLoadDone: false,
  showCopyLinkModal: false,
  showStreetmixIntroModal: false,
  shareableIntroModalShownOnce: false,
  shareableURLSearchParam: '',
  showLanduseErrModal: false,
  showNoFeatureSelectedModal: false,
  showConfirmDeleteModal: false,
  showClearSegmentsStartManualModal: false,
  notMatchingVariables: [],
  popupSelectedFeature: null,
  showErrAddingFeatureToProjectModal: false,
  showErrRemovingFeatureFromProjectModal: false,
  amenitiesNotSeenOnStreetmix: []
}

export const segmentSelectionStartUp = (state, action) => {
  const oidField = getOIDField()

  const incomingSegment = action.payload
  const currentSegments = state.selectedFeaturesArr
  const currentSegmentIds = new Set(
    currentSegments.map((p) => p?.attributes[oidField])
  )
  const incomingSegmentId = incomingSegment?.attributes[oidField]

  return {
    incomingSegment,
    currentSegments,
    currentSegmentIds,
    incomingSegmentId,

    oidField
  }
}

const appSlice = createSlice({
  name: 'appSlice',
  initialState,
  reducers: {
    setMaploaded(state, action) {
      state.mapLoaded = action.payload
    },
    setManualStreetMode(state, action) {
      state.manualStreetMode = action.payload
    },
    setSelectedFeature(state, action) {
      state.selectedFeature = action.payload
    },
    setSelectedFeaturesArr(state, action) {
      state.selectedFeaturesArr = action.payload
    },
    setOpenStreetMixDialog(state, action) {
      state.openStreetMixDialog = action.payload
    },
    setShowCircularProgress(state, action) {
      state.showCircularProgress = action.payload
    },
    setShowPopup(state, action) {
      state.showPopup = action.payload
    },
    setPopupExpanded(state, action) {
      state.popupExpanded = action.payload
    },
    setToolboxExpanded(state, action) {
      state.toolboxExpanded = action.payload
    },
    setShowStreetmixToaster(state, action) {
      state.showStreetmixToaster = action.payload
    },
    setShowSegmentAddedToaster(state, action) {
      state.showSegmentAddedToaster = action.payload
    },
    resetDefaultModalTiers(state, action) {
      state.defaultModalTiers = initialState.defaultModalTiers
    },
    setDefaultModalTiers(state, action) {
      state.defaultModalTiers = action.payload
    },
    setRoadScoreType(state, action) {
      state.roadScoreType = action.payload
    },
    setCriteriaElmScrollTop(state, action) {
      state.criteriaElmScrollTop = action.payload
    },
    setShowResetTiersModal(state, action) {
      state.showResetTiersModal = action.payload
    },
    setDownloadDataUrl(state, action) {
      state.downloadDataUrl = action.payload
    },
    setReportHeaderDataUrl(state, action) {
      state.reportHeaderDataUrl = action.payload
    },
    setReportTickImgDataUrl(state, action) {
      state.reportTickImgDataUrl = action.payload
    },
    setReportPedestrialImgUrl(state, action) {
      state.reportPedestrialImgUrl = action.payload
    },
    setReportBicycleImgUrl(state, action) {
      state.reportBicycleImgUrl = action.payload
    },
    setReportAutoImgUrl(state, action) {
      state.reportAutoImgUrl = action.payload
    },
    setReportFreightImgUrl(state, action) {
      state.reportFreightImgUrl = action.payload
    },
    setReportTransitImgUrl(state, action) {
      state.reportTransitImgUrl = action.payload
    },

    setShareableURL(state, action) {
      state.shareableURL = action.payload
    },
    setFirstAppLoadDone(state, action) {
      state.firstAppLoadDone = action.payload
    },
    setShowCopyLinkModal(state, action) {
      state.showCopyLinkModal = action.payload
    },
    setShowStreetmixIntroModal(state, action) {
      state.showStreetmixIntroModal = action.payload
    },
    setShareableIntroModalShownOnce(state, action) {
      state.shareableIntroModalShownOnce = action.payload
    },
    setShareableURLSearchParam(state, action) {
      state.shareableURLSearchParam = action.payload
    },
    setShowToolbox(state, action) {
      state.showToolbox = action.payload
    },
    setShowLanduseErrModal(state, action) {
      state.showLanduseErrModal = action.payload
    },
    setShowErrAddingFeatureToProjectModal(state, action) {
      state.showErrAddingFeatureToProjectModal = action.payload
    },
    setShowErrRemovingFeatureFromProjectModal(state, action) {
      state.showErrRemovingFeatureFromProjectModal = action.payload
    },
    setShowConfirmDeleteModal(state, action) {
      state.showConfirmDeleteModal = action.payload
    },
    setShowClearSegmentsStartManualModal(state, action) {
      state.showClearSegmentsStartManualModal = action.payload
    },
    addSelectedFeatures(state, action) {
      const info = segmentSelectionStartUp(state, action)

      const segmentsToAdd = !info.currentSegmentIds.has(info.incomingSegmentId)
        ? [info.incomingSegment]
        : []

      const updatedList = [...info.currentSegments].concat(segmentsToAdd)

      if (updatedList.length >= info.currentSegments.length) {
        // When  segment is removed, show tosater
        state.showSegmentAddedToaster = true
      }
      updateSelectedSegmentGraphics(updatedList)

      state.selectedFeaturesArr = updatedList
    },
    removeSelectedFeatures(state, action) {
      const info = segmentSelectionStartUp(state, action)
      const updatedList = info.currentSegments.filter((segment) => {
        return segment.attributes[info.oidField] !== info.incomingSegmentId
      })
      // if sharebale URL , Check if any of the segmetns were removed from the exsisting list
      // If exsisting segments were removed, then set shareableURL to false
      if (
        state.shareableURL &&
        updatedList.length !== info.currentSegments.length
      ) {
        state.shareableURL = false
      }

      if (updatedList.length !== info.currentSegments.length) {
        // Keep toolbox open , whenever user clicks on map
        // state.showToolbox = true

        // When  segment is removed, show tosater
        state.showSegmentAddedToaster = true
      }

      updateSelectedSegmentGraphics(updatedList)
      state.selectedFeaturesArr = updatedList
    },
    clearAllSelectedFeatures(state) {
      updateSelectedSegmentGraphics([])
      state.selectedFeaturesArr = []
    },
    setNotMatchingVariables(state, action) {
      state.notMatchingVariables = action.payload
    },
    setShowNoFeatureSelectedModal(state, action) {
      state.showNoFeatureSelectedModal = action.payload
    },
    setPopupSelectedFeature(state, action) {
      state.popupSelectedFeature = action.payload
    },
    setAmenitiesNotSeenOnStreetmix(state, action) {
      state.amenitiesNotSeenOnStreetmix =
        state.amenitiesNotSeenOnStreetmix.includes(action.payload)
          ? state.amenitiesNotSeenOnStreetmix.filter(
              (input) => input !== action.payload
            )
          : [...state.amenitiesNotSeenOnStreetmix, action.payload]
    }
  }
})

export const {
  setMaploaded,
  setManualStreetMode,
  setSelectedFeature,
  setOpenStreetMixDialog,
  setShowCircularProgress,
  setShowPopup,
  setShowStreetmixToaster,
  setShowResetTiersModal,
  setShowSegmentAddedToaster,
  setRoadScoreType,
  setDownloadDataUrl,
  setReportHeaderDataUrl,
  setReportTickImgDataUrl,
  setReportPedestrialImgUrl,
  setReportBicycleImgUrl,
  setReportFreightImgUrl,
  setReportAutoImgUrl,
  setReportTransitImgUrl,
  setShareableURL,
  setFirstAppLoadDone,
  setShowCopyLinkModal,
  setShowStreetmixIntroModal,
  setShareableIntroModalShownOnce,
  setShareableURLSearchParam,
  setShowToolbox,
  addSelectedFeatures,
  setSelectedFeaturesArr,
  setShowConfirmDeleteModal,
  setShowClearSegmentsStartManualModal,
  removeSelectedFeatures,
  clearAllSelectedFeatures,
  setShowLanduseErrModal,
  setNotMatchingVariables,
  setShowNoFeatureSelectedModal,
  setCriteriaElmScrollTop,
  setDefaultModalTiers,
  resetDefaultModalTiers,
  setPopupSelectedFeature,
  setShowErrAddingFeatureToProjectModal,
  setShowErrRemovingFeatureFromProjectModal,
  setPopupExpanded,
  setToolboxExpanded,
  setAmenitiesNotSeenOnStreetmix
} = appSlice.actions
export default appSlice.reducer
